import axios from "axios";
import { Message } from "./dto/message";
import { Thread } from "./dto/thread";
import { TranscribeResponse } from "./dto/transcribe";

const api = {
  ask: (
    query: string,
    messageId: string,
    threadId: string,
    onMessage: (msg: string) => void,
    token: string,
    onError?: (err: unknown) => void
  ) =>
    fetch(import.meta.env.VITE_BACKEND_URL + "/v1.1/chat/", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        query,
        message_id: messageId,
        thread_id: threadId,
      }),
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`Network error: ${response.statusText}`);
      }

      if (!response.body) {
        throw new Error("No body");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer: string = "";
      let hasAnyEvent = false;

      function processStream() {
        reader
          .read()
          .then(({ done, value }) => {
            if (done) {
              // If the stream ends without any SSE events, consider it an error scenario
              if (!hasAnyEvent) {
                onError?.(new Error("Stream ended without SSE events"));
              }
              return;
            }

            buffer += decoder.decode(value, { stream: true });

            const parts = buffer.split("\n");

            for (let i = 0; i < parts.length - 1; i++) {
              const part = parts[i].trim();
              if (part.startsWith("data:")) {
                hasAnyEvent = true;
                onMessage(part.replace(/^data:\s*/, ""));
              }
            }

            buffer = parts[parts.length - 1].endsWith("\n\n")
              ? ""
              : parts[parts.length - 1];
            processStream();
          })
          .catch((error) => {
            console.error("Error reading stream:", error);
            onError?.(error);
          });
      }

      processStream();
    }),
  feedback: async (
    messageId: string,
    feedback: "liked" | "disliked" | "none",
    token: string
  ) => {
    const response = await fetch(
      import.meta.env.VITE_BACKEND_URL + "/feedback",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ message_id: messageId, feedback }),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to send feedback");
    }

    return response.json();
  },
  askWebSearch: (
    query: string,
    messageId: string,
    threadId: string,
    onMessage: (msg: string) => void,
    token: string
  ) =>
    fetch(import.meta.env.VITE_BACKEND_URL + "/v1.1/web-search/", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        query,
        message_id: messageId,
        thread_id: threadId,
      }),
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`Network error: ${response.statusText}`);
      }
      if (!response.body) {
        throw new Error("No body");
      }
      const reader = response.body.getReader();

      const decoder = new TextDecoder("utf-8");
      let buffer: string = "";

      function processStream() {
        reader
          .read()
          .then(({ done, value }) => {
            if (done) {
              return;
            }
            buffer += decoder.decode(value, { stream: true });
            const parts = buffer.split("\n");
            for (let i = 0; i < parts.length - 1; i++) {
              const part = parts[i].trim();
              if (part.startsWith("data:")) {
                onMessage(part.replace(/^data:\s*/, ""));
              }
            }
            buffer = parts[parts.length - 1].endsWith("\n\n")
              ? ""
              : parts[parts.length - 1];
            processStream();
          })
          .catch((error) => {
            console.error("Error reading stream:", error);
          });
      }
      processStream();
    }),

  upload: async (
    files: File[],
    token: string,
    targetFolder: string
  ): Promise<{
    message: string;
    uploaded: Array<{ filename: string; folder: string }>;
    failed: Array<{ filename: string; reason: string }>;
  }> => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });
    formData.append("target_folder", targetFolder);

    const response = await fetch(
      import.meta.env.VITE_BACKEND_URL + "/documents/upload",
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      }
    );

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ detail: "Upload failed" }));
      throw new Error(
        errorData.detail || `Upload failed with status ${response.status}`
      );
    }

    return response.json();
  },
  getDocuments: (
    token: string,
    page: number = 1,
    name: string | null = null,
    sortField: string | null = null,
    sortDirection: string | null = null,
    folderPath: string | null = null
  ) => {
    let offset = 0;
    if (page > 1) {
      offset = 10 * (page - 1);
    }
    let queryString = "";
    if (name) {
      queryString += `&name=${name}`;
    }
    if (sortField) {
      queryString += `&sort_field=${sortField}`;
    }
    if (sortDirection) {
      queryString += `&sort_direction=${sortDirection}`;
    }
    if (folderPath) {
      queryString += `&folder_path=${encodeURIComponent(folderPath)}`;
    }
    return fetch(
      `${
        import.meta.env.VITE_BACKEND_URL
      }/documents?offset=${offset}${queryString}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Network error: ${response.statusText}`);
        }
        return response.json();
      })
      .then((data) => ({
        documents: data.documents,
        totalPages: data.total_pages, // Ensure API returns total pages
        totalDocuments: data.total_documents,
        nextPage: data.next_page,
        previousPage: data.previous_page,
      }))
      .catch((error) => {
        console.error("Error fetching documents:", error);
        return {
          documents: [],
          totalPages: 1,
          totalDocuments: 0,
          nextPage: null,
          previousPage: null,
        };
      });
  },
  getDocumentTree: async (token: string) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_URL}/documents/tree`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to fetch document tree");
    }

    return response.json();
  },
  deleteDocuments: (documentId: string, token: string) => {
    return fetch(
      `${import.meta.env.VITE_BACKEND_URL}/documents/delete/${documentId}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Network error: ${response.statusText}`);
        }
        return response.json();
      })
      .catch((error) => {
        console.error("Error deleting documents:", error);
        return [];
      });
  },
  fetchMessages: (
    token: string,
    page: number = 1,
    userFeedback: string | null = null,
    adminFeedback: string | null = null,
    edited: boolean | null = null,
    hasComment: boolean | null = null,
    filterUserId: string | null = null,
    archivedFilter: string = "all",
    qrCodeId: string | null = null,
    assistantId: number | null = null
  ) => {
    let offset = 0;
    let queryString = "";
    if (page > 1) {
      offset = 10 * (page - 1);
    }
    if (userFeedback) {
      queryString += `&user_feedback=${userFeedback}`;
    }
    if (adminFeedback) {
      queryString += `&admin_feedback=${adminFeedback}`;
    }
    if (edited !== null) {
      queryString += `&edited=${edited}`;
    }
    if (hasComment !== null) {
      queryString += `&has_comment=${hasComment}`;
    }
    if (filterUserId) {
      queryString += `&filter_user_id=${filterUserId}`;
    }
    if (archivedFilter !== "all") {
      queryString += `&archived_filter=${archivedFilter}`;
    }
    if (qrCodeId) {
      queryString += `&qr_code_id=${qrCodeId}`;
    }
    if (assistantId !== null && assistantId !== undefined) {
      queryString += `&assistant_id=${assistantId}`;
    }
    return fetch(
      `${
        import.meta.env.VITE_BACKEND_URL
      }/messages/?offset=${offset}${queryString}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Network error: ${response.statusText}`);
        }
        return response.json();
      })
      .then((data) => ({
        messages: data.messages,
        totalPages: data.total_pages, // Ensure API returns total pages
        nextPage: data.next_page,
        previousPage: data.previous_page,
      }))
      .catch((error) => {
        console.error("Error fetching messages:", error);
        return {
          messages: [],
          totalPages: 1,
          nextPage: null,
          previousPage: null,
        };
      });
  },
  fetchMessageUsers: (token: string) => {
    return fetch(`${import.meta.env.VITE_BACKEND_URL}/messages/users`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Network error: ${response.statusText}`);
        }
        return response.json();
      })
      .catch((error) => {
        console.error("Error fetching message users:", error);
        return { users: [] };
      });
  },
  fetchQRCodes: (token: string) => {
    return fetch(`${import.meta.env.VITE_BACKEND_URL}/qr-codes`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Network error: ${response.statusText}`);
        }
        return response.json();
      })
      .catch((error) => {
        console.error("Error fetching QR codes:", error);
        return { qr_codes: [] };
      });
  },
  getUsers: (
    token: string,
    offset: number = 0,
    limit: number = 10,
    filter: string | null = null,
    sortField: string | null = null,
    sortDirection: string | null = null
  ) => {
    let url = `${
      import.meta.env.VITE_BACKEND_URL
    }/users?offset=${offset}&limit=${limit}`;
    if (filter) {
      url += `&filter=${encodeURIComponent(filter)}`;
    }
    if (sortField) {
      url += `&sort_field=${sortField}`;
    }
    if (sortDirection) {
      url += `&sort_direction=${sortDirection}`;
    }

    return fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Network error: ${response.statusText}`);
        }
        return response.json();
      })
      .then((data) => {
        // Backend now returns the expected format directly
        if (data && data.users) {
          return data;
        } else {
          console.error("Unexpected API response format:", data);
          return {
            users: {
              data: [],
              total_pages: 1,
              next_page: null,
              previous_page: null,
              total_users: 0,
            },
          };
        }
      })
      .catch((error) => {
        console.error("Error fetching users:", error);
        return {
          users: {
            data: [],
            total_pages: 1,
            next_page: null,
            previous_page: null,
            total_users: 0,
          },
        };
      });
  },
  addUser: (userData: UserCreateRequest, token: string) => {
    return fetch(import.meta.env.VITE_BACKEND_URL + "/users", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(userData),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Network error: ${response.statusText}`);
        }
        return response.json();
      })
      .catch((error) => {
        console.error("Error adding user:", error);
        throw error;
      });
  },
  deleteUser: (userId: string, token: string) => {
    return fetch(`${import.meta.env.VITE_BACKEND_URL}/users/${userId}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Network error: ${response.statusText}`);
        }
        return response.json();
      })
      .catch((error) => {
        console.error("Error deleting user:", error);
        return null;
      });
  },
  updateUserRole: (userId: string, role: string, token: string) => {
    return fetch(`${import.meta.env.VITE_BACKEND_URL}/users/${userId}/role`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ role }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Network error: ${response.statusText}`);
        }
        return response.json();
      })
      .catch((error) => {
        console.error("Error updating user role:", error);
        throw error;
      });
  },
  updateUser: (userId: string, userData: UserUpdateRequest, token: string) => {
    return fetch(`${import.meta.env.VITE_BACKEND_URL}/users/${userId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(userData),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Network error: ${response.statusText}`);
        }
        return response.json();
      })
      .catch((error) => {
        console.error("Error updating user:", error);
        return null;
      });
  },
  updateMessage: (
    messageId: number,
    token: string,
    newAnswer: string | undefined,
    adminFeedback?: "liked" | "disliked" | "none"
  ) => {
    const body: Record<string, unknown> = {};
    if (newAnswer !== undefined) {
      body["new_answer"] = newAnswer;
    }
    if (adminFeedback !== undefined) {
      body["admin_feedback"] = adminFeedback;
    }
    return fetch(
      `${import.meta.env.VITE_BACKEND_URL}/edit-answer/${messageId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Network error: ${response.statusText}`);
        }
        return response.json();
      })
      .catch((error) => {
        console.error("Error updating message:", error);
        return null;
      });
  },
  getThreads: async (
    token: string,
    offset: number = 0,
    limit: number = 20
  ): Promise<{ threads: Thread[] }> => {
    const response = await fetch(
      `${
        import.meta.env.VITE_BACKEND_URL
      }/threads?offset=${offset}&limit=${limit}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to fetch threads");
    }

    return response.json();
  },
  getThreadMessages: async (
    threadId: string,
    token: string
  ): Promise<{ messages: Message[] }> => {
    const response = await fetch(
      import.meta.env.VITE_BACKEND_URL + `/threads/${threadId}/messages`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to fetch thread messages");
    }

    return response.json();
  },

  archiveThread: async (
    threadId: string,
    token: string
  ): Promise<{ message: string; thread_id: string }> => {
    const response = await fetch(
      import.meta.env.VITE_BACKEND_URL + `/threads/${threadId}/archive`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to archive thread");
    }

    return response.json();
  },

  unarchiveThread: async (
    threadId: string,
    token: string
  ): Promise<{ message: string; thread_id: string }> => {
    const response = await fetch(
      import.meta.env.VITE_BACKEND_URL + `/threads/${threadId}/unarchive`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to unarchive thread");
    }

    return response.json();
  },

  addCommentToMessage: async (
    messageId: string,
    comment: string,
    token: string
  ): Promise<Message> => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_URL}/messages/${messageId}/comment`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ comment }),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to add comment");
    }

    return response.json();
  },

  deleteCommentFromMessage: async (
    messageId: string,
    token: string
  ): Promise<Message> => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_URL}/messages/${messageId}/comment`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to delete comment");
    }

    return response.json();
  },

  getCurrentUser: async (token: string) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_URL}/users/me`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to get current user");
    }

    return response.json();
  },

  getAnalyticsOverview: async (
    token: string,
    params: {
      startDate?: string;
      endDate?: string;
      interval?: "day" | "week" | "month";
      perUserLimit?: number;
      perUserOffset?: number;
      perUserSortBy?: "interactions" | "feedback_total" | "feedback_ratio" | "last_active";
      perUserSortDir?: "asc" | "desc";
    } = {},
  ) => {
    const query = new URLSearchParams();
    if (params.startDate) {
      query.append("start_date", params.startDate);
    }
    if (params.endDate) {
      query.append("end_date", params.endDate);
    }
    if (params.interval) {
      query.append("interval", params.interval);
    }
    if (params.perUserLimit !== undefined) {
      query.append("per_user_limit", params.perUserLimit.toString());
    }
    if (params.perUserOffset !== undefined) {
      query.append("per_user_offset", params.perUserOffset.toString());
    }
    if (params.perUserSortBy) {
      query.append("per_user_sort_by", params.perUserSortBy);
    }
    if (params.perUserSortDir) {
      query.append("per_user_sort_dir", params.perUserSortDir);
    }
    const queryString = query.toString();
    const url = `${
      import.meta.env.VITE_BACKEND_URL
    }/analytics/overview${queryString ? `?${queryString}` : ""}`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error("Failed to load analytics");
    }

    return response.json();
  },

  updateCurrentUser: async (userData: UserUpdateRequest, token: string) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_URL}/users/me`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(userData),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to update current user");
    }

    return response.json();
  },

  uploadProfileImage: async (file: File, token: string) => {
    const formData = new FormData();
    formData.append("file", file);

    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_URL}/users/me/profile-image`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      }
    );

    if (!response.ok) {
      throw new Error("Failed to upload profile image");
    }

    return response.json();
  },

  transcribe: async (
    audioBlob: Blob,
    token: string
  ): Promise<TranscribeResponse> => {
    const formData = new FormData();

    // Use the actual MIME type to determine file extension
    let filename = "recording.webm"; // default fallback
    if (audioBlob.type.includes("webm")) {
      filename = "recording.webm";
    } else if (audioBlob.type.includes("mp4")) {
      filename = "recording.mp4";
    } else if (audioBlob.type.includes("ogg")) {
      filename = "recording.ogg";
    } else if (audioBlob.type.includes("wav")) {
      filename = "recording.wav";
    }

    console.log("Uploading audio file:", {
      filename,
      mimeType: audioBlob.type,
      size: audioBlob.size,
    });

    formData.append("file", audioBlob, filename);

    return (
      await axios.post<TranscribeResponse>(
        `${import.meta.env.VITE_BACKEND_URL}/transcribe`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      )
    ).data;
  },
  // Assistant Settings API
  uploadAssistantPhoto: async (file: File, token: string) => {
    const formData = new FormData();
    formData.append("file", file);

    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_URL}/assistant-settings/assistant-photo`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      }
    );

    if (!response.ok) {
      throw new Error(
        `Failed to upload assistant photo: ${response.statusText}`
      );
    }

    return response.json();
  },

  getAssistantPhoto: async (token: string) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_URL}/assistant-settings/assistant-photo`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to get assistant photo: ${response.statusText}`);
    }

    return response.json();
  },

  getAvailableAssistantPhotos: async (token: string) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_URL}/assistant-settings/available-photos`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(
        `Failed to get available assistant photos: ${response.statusText}`
      );
    }

    return response.json();
  },

  setAssistantPhoto: async (photoUrl: string, token: string) => {
    const response = await fetch(
      `${
        import.meta.env.VITE_BACKEND_URL
      }/assistant-settings/set-assistant-photo`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ photo_url: photoUrl }),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to set assistant photo: ${response.statusText}`);
    }

    return response.json();
  },

  // Assistants CRUD
  getAssistants: async (token: string) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_URL}/assistants`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    if (!response.ok) {
      throw new Error(`Failed to fetch assistants: ${response.statusText}`);
    }
    return response.json();
  },

  createAssistant: async (
    name: string,
    description: string | undefined,
    token: string
  ) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_URL}/assistants`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name, description }),
      },
    );
    if (!response.ok) {
      throw new Error(`Failed to create assistant: ${response.statusText}`);
    }
    return response.json();
  },

  updateAssistant: async (
    assistantId: number,
    updates: {
      name?: string;
      sharepoint_root_folder?: string;
      welcome_message?: string | null;
      description?: string | null;
    },
    token: string
  ) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_URL}/assistants/${assistantId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updates),
      },
    );
    if (!response.ok) {
      throw new Error(`Failed to update assistant: ${response.statusText}`);
    }
    return response.json();
  },

  deleteAssistant: async (assistantId: number, token: string) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_URL}/assistants/${assistantId}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    if (!response.ok) {
      throw new Error(`Failed to delete assistant: ${response.statusText}`);
    }
    return response.json();
  },

  uploadAssistantPhotoForId: async (
    assistantId: number,
    file: File,
    token: string,
  ) => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_URL}/assistants/${assistantId}/photo`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      },
    );
    if (!response.ok) {
      throw new Error(`Failed to upload assistant photo: ${response.statusText}`);
    }
    return response.json();
  },

  setCurrentAssistant: async (assistantId: number | null, token: string) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_URL}/assistants/current`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ assistant_id: assistantId }),
      },
    );
    if (!response.ok) {
      throw new Error(`Failed to set current assistant: ${response.statusText}`);
    }
    return response.json();
  },

  // Meeting Templates API
  getMeetingTemplates: async (token: string) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_URL}/meeting-templates`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    if (!response.ok) {
      throw new Error(`Failed to get meeting templates: ${response.statusText}`);
    }
    return response.json();
  },

  createMeetingTemplate: async (
    name: string,
    content: string,
    token: string,
  ) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_URL}/meeting-templates`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name, content }),
      },
    );
    if (!response.ok) {
      throw new Error(`Failed to create meeting template: ${response.statusText}`);
    }
    return response.json();
  },

  updateMeetingTemplate: async (
    templateId: string,
    updates: { name?: string; content?: string },
    token: string,
  ) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_URL}/meeting-templates/${templateId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updates),
      },
    );
    if (!response.ok) {
      throw new Error(`Failed to update meeting template: ${response.statusText}`);
    }
    return response.json();
  },

  deleteMeetingTemplate: async (templateId: string, token: string) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_URL}/meeting-templates/${templateId}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    if (!response.ok) {
      throw new Error(`Failed to delete meeting template: ${response.statusText}`);
    }
    return response.json();
  },

  // Meetings API
  transcribeMeeting: async (audioBlob: Blob, language: string, token: string) => {
    const formData = new FormData();

    // Determine filename based on MIME type
    let filename = "meeting.webm";
    if (audioBlob.type.includes("webm")) {
      filename = "meeting.webm";
    } else if (audioBlob.type.includes("mp4")) {
      filename = "meeting.mp4";
    } else if (audioBlob.type.includes("ogg")) {
      filename = "meeting.ogg";
    } else if (audioBlob.type.includes("wav")) {
      filename = "meeting.wav";
    }

    formData.append("file", audioBlob, filename);

    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_URL}/meetings/transcribe?language=${encodeURIComponent(language)}`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      },
    );
    if (!response.ok) {
      throw new Error(`Failed to transcribe meeting: ${response.statusText}`);
    }
    return response.json();
  },

  generateMeetingNote: async (
    title: string,
    transcript: object,
    templateId: string | null,
    audioDuration: number | null,
    language: string,
    token: string,
  ) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_URL}/meetings/generate`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          title,
          transcript,
          template_id: templateId,
          audio_duration: audioDuration,
          language,
        }),
      },
    );
    if (!response.ok) {
      throw new Error(`Failed to generate meeting note: ${response.statusText}`);
    }
    return response.json();
  },

  getMeetingNotes: async (
    page: number = 1,
    pageSize: number = 20,
    search?: string,
    token?: string,
  ) => {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });
    if (search) {
      params.append("search", search);
    }

    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_URL}/meetings?${params.toString()}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    if (!response.ok) {
      throw new Error(`Failed to get meeting notes: ${response.statusText}`);
    }
    return response.json();
  },

  getMeetingNote: async (noteId: string, token: string) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_URL}/meetings/${noteId}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    if (!response.ok) {
      throw new Error(`Failed to get meeting note: ${response.statusText}`);
    }
    return response.json();
  },

  updateMeetingNote: async (
    noteId: string,
    updates: { title?: string; generated_note?: string },
    token: string,
  ) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_URL}/meetings/${noteId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updates),
      },
    );
    if (!response.ok) {
      throw new Error(`Failed to update meeting note: ${response.statusText}`);
    }
    return response.json();
  },

  deleteMeetingNote: async (noteId: string, token: string) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_URL}/meetings/${noteId}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    if (!response.ok) {
      throw new Error(`Failed to delete meeting note: ${response.statusText}`);
    }
    return response.json();
  },

  // ------------------------------------------------------------------
  // Costs
  // ------------------------------------------------------------------

  getCostsOverview: async (
    token: string,
    params: { startDate?: string; endDate?: string } = {},
  ) => {
    const query = new URLSearchParams();
    if (params.startDate) query.append("start_date", params.startDate);
    if (params.endDate) query.append("end_date", params.endDate);
    const qs = query.toString();
    const url = `${import.meta.env.VITE_BACKEND_URL}/costs/overview${qs ? `?${qs}` : ""}`;
    const response = await fetch(url, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.ok) throw new Error("Failed to load costs overview");
    return response.json();
  },

  getInfrastructureCosts: async (
    token: string,
    params: { startDate?: string; endDate?: string } = {},
  ) => {
    const query = new URLSearchParams();
    if (params.startDate) query.append("start_date", params.startDate);
    if (params.endDate) query.append("end_date", params.endDate);
    const qs = query.toString();
    const url = `${import.meta.env.VITE_BACKEND_URL}/costs/infrastructure${qs ? `?${qs}` : ""}`;
    const response = await fetch(url, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.ok) throw new Error("Failed to load infrastructure costs");
    return response.json();
  },

  createMonthlyInfraCost: async (
    token: string,
    data: {
      year: number;
      month: number;
      category: string;
      monthly_amount_chf: number;
      description?: string;
    },
  ) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_URL}/costs/infrastructure/monthly`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      },
    );
    if (!response.ok) throw new Error("Failed to create monthly infrastructure cost");
    return response.json();
  },

  deleteMonthlyInfraCost: async (
    token: string,
    year: number,
    month: number,
    category: string,
  ) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_URL}/costs/infrastructure/monthly/${year}/${month}/${category}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    if (!response.ok) throw new Error("Failed to delete monthly infrastructure cost");
    return response.json();
  },

  bulkImportInfraCosts: async (
    token: string,
    items: Array<{
      cost_date: string;
      category: string;
      amount_chf: number;
      source_ref: string;
      notes?: string;
    }>,
  ) => {
    const response = await fetch(
      `${import.meta.env.VITE_BACKEND_URL}/costs/infrastructure/bulk`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ items }),
      },
    );
    if (!response.ok) throw new Error("Failed to bulk import infrastructure costs");
    return response.json();
  },
};

interface UserCreateRequest {
  first_name: string;
  last_name: string;
  email: string;
  organization_id: string;
  role: string;
}

interface UserUpdateRequest {
  first_name: string;
  last_name: string;
}

export default api;
